// src/components/Reports/ItemsDetailReport.jsx
// ======================================================================
// تقرير أصناف تفصيلي عام — سطر لكل صنف في كل فاتورة
// يُستخدم للمبيعات والمشتريات بتمرير config مختلف (انظر SALES_DETAIL و PURCHASE_DETAIL أسفل الملف)
// المرتجعات تأتي من السيرفر بقيم سالبة فتُطرح من الإجماليات تلقائيًا
// ======================================================================

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Autocomplete, TextField, Typography, Box, Alert } from "@mui/material";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import GridOnIcon from "@mui/icons-material/GridOn";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

import axios from "../../axios";
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, useAllowedBranches, reportGridSx, fieldSx, fmtMoney, fmtNumber,
} from "./ReportKit";

const num = (color, bold = 700) => (p) => (
  <Typography sx={{
    direction: "ltr", fontWeight: bold, fontSize: 13.5, fontVariantNumeric: "tabular-nums",
    color: Number(p.value) < 0 ? "#be123c" : color,
  }}>
    {fmtMoney(p.value)}
  </Typography>
);

// ───────────────────────── الإعدادات الجاهزة ─────────────────────────
export const SALES_DETAIL = {
  title: "مبيعات الأصناف التفصيلي",
  subtitle: "كل صنف مباع في كل فاتورة — المرتجعات تظهر بالسالب وتُطرح من الإجماليات",
  endpoint: "/Invoice/SalesItemsDetail",
  partyLabel: "العميل",
  netLabel: "صافي المبيعات",
  qtyLabel: "صافي الكميات",
  returnType: 3,
  typeOptions: [
    { value: "1", label: "مبيعات" },
    { value: "11", label: "نقاط البيع" },
    { value: "3", label: "مرتجع مبيعات" },
  ],
  typeStyle: {
    1: { bg: "#e0e7ff", fg: "#4338ca" },
    11: { bg: "#e0f2fe", fg: "#0369a1" },
    3: { bg: "#ffe4e6", fg: "#be123c" },
  },
  openUrl: (r) => (r.invoiceType === 11 ? `/invoices/printPos/${r.invGlobalID}` : `/invoices/view/${r.invGlobalID}`),
  excelName: "SalesItemsDetail",
  sheetName: "مبيعات الأصناف تفصيلي",
};

export const PURCHASE_DETAIL = {
  title: "مشتريات الأصناف التفصيلي",
  subtitle: "كل صنف مشترى في كل فاتورة — مرتجعات المشتريات تظهر بالسالب وتُطرح من الإجماليات",
  endpoint: "/Invoice/PurchaseItemsDetail",
  partyLabel: "المورد",
  netLabel: "صافي المشتريات",
  qtyLabel: "صافي الكميات",
  returnType: 4,
  showReference: true, // رقم فاتورة المورد
  typeOptions: [
    { value: "2", label: "مشتريات" },
    { value: "4", label: "مرتجع مشتريات" },
  ],
  typeStyle: {
    2: { bg: "#d1fae5", fg: "#047857" },
    4: { bg: "#ffe4e6", fg: "#be123c" },
  },
  openUrl: (r) => `/invoices/view/${r.invGlobalID}`,
  excelName: "PurchaseItemsDetail",
  sheetName: "مشتريات الأصناف تفصيلي",
};

// ───────────────────────── المكوّن ─────────────────────────
export default function ItemsDetailReport({ config }) {
  const companyId = parseInt(localStorage.getItem("companyId"), 10);

  const range = useReportDateRange("today");
  const { branchOptions, defaultBranch, ready, branchName } = useAllowedBranches(companyId);

  const [branchId, setBranchId] = useState("");
  const [stockId, setStockId] = useState("");
  const [invoiceType, setInvoiceType] = useState("");
  const [product, setProduct] = useState(null);
  const [search, setSearch] = useState("");

  const [stocks, setStocks] = useState([]);
  const [products, setProducts] = useState([]);
  const [rows, setRows] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    axios.get(`/Stock/by-company/${companyId}`).then(r => setStocks(r.data || [])).catch(() => {});
    axios.get(`/Product/by-company/${companyId}`).then(r => setProducts(r.data || [])).catch(() => {});
  }, [companyId]);

  useEffect(() => { if (ready) setBranchId(defaultBranch); }, [ready, defaultBranch]);

  const fetchReport = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get(config.endpoint, {
        params: {
          companyId,
          dateFrom: range.dateFrom,
          dateTo: range.dateTo,
          branchId: branchId ? Number(branchId) : null,
          stockId: stockId ? Number(stockId) : null,
          productId: product?.productId || null,
          invoiceType: invoiceType ? Number(invoiceType) : null,
        },
      });
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setTruncated(!!res.data?.truncated);
    } catch {
      toast.error("فشل تحميل التقرير");
    } finally {
      setLoading(false);
    }
  }, [companyId, config.endpoint, range.dateFrom, range.dateTo, branchId, stockId, product, invoiceType]);

  useEffect(() => { if (ready) fetchReport(); }, [ready, fetchReport]);

  // اسم الطرف موحّد: partyName (المشتريات) أو customerName (المبيعات)
  const partyOf = (r) => r.partyName ?? r.customerName ?? "—";

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      String(r.invoiceNumber ?? "").toLowerCase().includes(q) ||
      String(r.referenceNumber ?? "").toLowerCase().includes(q) ||
      (r.productNameAr ?? "").toLowerCase().includes(q) ||
      (r.productNameEn ?? "").toLowerCase().includes(q) ||
      partyOf(r).toLowerCase().includes(q));
  }, [rows, search]);

  const totals = useMemo(() => {
    const t = { qty: 0, gross: 0, discount: 0, vat: 0, net: 0, returns: 0 };
    const invoices = new Set();
    for (const r of visibleRows) {
      t.qty += Number(r.quantity) || 0;
      t.gross += Number(r.gross) || 0;
      t.discount += Number(r.discount) || 0;
      t.vat += Number(r.vat) || 0;
      t.net += Number(r.net) || 0;
      if (r.invoiceType === config.returnType) t.returns += Math.abs(Number(r.net) || 0);
      invoices.add(r.invGlobalID);
    }
    t.invoices = invoices.size;
    return t;
  }, [visibleRows, config.returnType]);

  const columns = useMemo(() => [
    {
      field: "issueDate", headerName: "التاريخ", width: 135, headerAlign: "center", align: "center",
      renderCell: (p) => (
        <Box sx={{ textAlign: "center", lineHeight: 1.3 }}>
          <Typography sx={{ direction: "ltr", fontSize: 13.5, fontWeight: 600 }}>{dayjs(p.value).format("YYYY-MM-DD")}</Typography>
          <Typography sx={{ direction: "ltr", fontSize: 11.5, color: "#94a3b8" }}>{dayjs(p.value).format("HH:mm")}</Typography>
        </Box>
      ),
    },
    {
      field: "invoiceNumber", headerName: "الفاتورة", width: 130, headerAlign: "center", align: "center",
      renderCell: (p) => (
        <Typography
          onClick={() => window.open(config.openUrl(p.row), "_blank")}
          sx={{ direction: "ltr", fontWeight: 800, fontSize: 13.5, color: "#4f46e5", cursor: "pointer", '&:hover': { textDecoration: "underline" } }}
        >
          {p.value}
        </Typography>
      ),
    },
    ...(config.showReference ? [{
      field: "referenceNumber", headerName: "فاتورة المورد", width: 120, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ direction: "ltr", fontSize: 13, color: "#475569" }}>{p.value || "—"}</Typography>,
    }] : []),
    {
      field: "invoiceTypeName", headerName: "النوع", width: 105, headerAlign: "center", align: "center",
      renderCell: (p) => {
        const s = config.typeStyle[p.row.invoiceType] ?? { bg: "#f1f5f9", fg: "#475569" };
        return <Box sx={{ px: 1.1, py: .3, borderRadius: "999px", fontSize: 12, fontWeight: 800, bgcolor: s.bg, color: s.fg }}>{p.value}</Box>;
      },
    },
    {
      field: "party", headerName: config.partyLabel, flex: 1, minWidth: 140, headerAlign: "center", align: "center",
      valueGetter: (...args) => {
        // متوافق مع الإصدار 6 (params) والإصدار 7 (value, row)
        const row = args[1] ?? args[0]?.row;
        return row ? partyOf(row) : "";
      },
    },
    {
      field: "productNameAr", headerName: "الصنف", flex: 1.4, minWidth: 180, headerAlign: "center", align: "center",
      renderCell: (p) => <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{p.value || p.row.productNameEn || "—"}</Typography>,
    },
    { field: "unitName", headerName: "الوحدة", width: 90, headerAlign: "center", align: "center" },
    {
      field: "quantity", headerName: "الكمية", type: "number", width: 90, headerAlign: "center", align: "center",
      renderCell: (p) => (
        <Typography sx={{ direction: "ltr", fontWeight: 800, fontSize: 13.5, color: p.value < 0 ? "#be123c" : "#0f172a" }}>
          {fmtNumber(p.value)}
        </Typography>
      ),
    },
    { field: "unitPrice", headerName: "السعر", type: "number", width: 105, headerAlign: "center", align: "center", renderCell: num("#334155", 600) },
    { field: "gross", headerName: "الإجمالي", type: "number", width: 115, headerAlign: "center", align: "center", renderCell: num("#0f172a") },
    { field: "discount", headerName: "الخصم", type: "number", width: 100, headerAlign: "center", align: "center", renderCell: num("#e11d48") },
    { field: "vat", headerName: "الضريبة", type: "number", width: 100, headerAlign: "center", align: "center", renderCell: num("#d97706") },
    { field: "net", headerName: "الصافي", type: "number", width: 120, headerAlign: "center", align: "center", renderCell: num("#059669", 800) },
    { field: "stockName", headerName: "المخزن", width: 120, headerAlign: "center", align: "center" },

  ], [config]);

  const exportExcel = () => {
    if (!visibleRows.length) return toast.info("لا توجد بيانات للتصدير");
    const data = visibleRows.map(r => ({
      "التاريخ": dayjs(r.issueDate).format("YYYY-MM-DD HH:mm"),
      "رقم الفاتورة": r.invoiceNumber,
      ...(config.showReference ? { "فاتورة المورد": r.referenceNumber || "" } : {}),
      "النوع": r.invoiceTypeName,
      [config.partyLabel]: partyOf(r),
      "الصنف": r.productNameAr || r.productNameEn,
      "الوحدة": r.unitName,
      "الكمية": r.quantity,
      "السعر": r.unitPrice,
      "الإجمالي": r.gross,
      "الخصم": r.discount,
      "الضريبة": r.vat,
      "الصافي": r.net,
      "المخزن": r.stockName,
    }));
    data.push({
      "التاريخ": "", "رقم الفاتورة": "", "الصنف": "الإجمالي",
      "الكمية": totals.qty, "الإجمالي": totals.gross, "الخصم": totals.discount,
      "الضريبة": totals.vat, "الصافي": totals.net,
    });
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!views"] = [{ RTL: true }];
    ws["!cols"] = Object.keys(data[0]).map(k => ({ wch: k === "الصنف" ? 30 : k === config.partyLabel ? 22 : 13 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, config.sheetName.slice(0, 31));
    XLSX.writeFile(wb, `${config.excelName}_${range.dateFrom}_${range.dateTo}.xlsx`);
  };

  const resetFilters = () => {
    range.reset();
    setBranchId(defaultBranch);
    setStockId("");
    setInvoiceType("");
    setProduct(null);
    setSearch("");
  };

  const bName = branchName(branchId);
  const activeCount = (branchId ? 1 : 0) + (stockId ? 1 : 0) + (invoiceType ? 1 : 0) + (product ? 1 : 0) + (search ? 1 : 0);

  return (
    <ReportPage
      title={config.title}
      subtitle={config.subtitle}
      icon={<ListAltOutlinedIcon />}
      period={range}
      badges={bName ? [{ label: bName, icon: <StorefrontOutlinedIcon /> }] : []}
      actions={[
        { label: "تحديث", icon: <RefreshIcon />, onClick: fetchReport, disabled: loading },
        { label: "Excel", icon: <GridOnIcon />, onClick: exportExcel, primary: true },
      ]}
      stats={[
        { label: "عدد الفواتير", value: fmtNumber(totals.invoices), icon: <ReceiptLongIcon />, color: "primary" },
        { label: config.qtyLabel, value: fmtNumber(totals.qty), icon: <ShoppingCartOutlinedIcon />, color: "slate" },
        { label: "الإجمالي", value: fmtMoney(totals.gross), icon: <AttachMoneyIcon />, color: "info" },
        { label: "الخصومات", value: fmtMoney(totals.discount), icon: <LocalOfferIcon />, color: "warning" },
        { label: "الضريبة", value: fmtMoney(totals.vat), icon: <RequestQuoteOutlinedIcon />, color: "error" },
        { label: "المرتجعات", value: fmtMoney(totals.returns), icon: <AssignmentReturnOutlinedIcon />, color: "error" },
        { label: config.netLabel, value: fmtMoney(totals.net), icon: <AccountBalanceWalletIcon />, color: "success", highlight: true },
      ]}
      filters={
        <ReportFilters
          range={range}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={`رقم الفاتورة، الصنف، ${config.partyLabel}...`}
          onApply={fetchReport}
          onReset={resetFilters}
          loading={loading}
          activeCount={activeCount}
        >
          <ReportSelect label="الفرع" value={branchId} onChange={setBranchId} options={branchOptions} />
          <ReportSelect
            label="المخزن" value={stockId} onChange={setStockId}
            options={stocks.map(s => ({ value: String(s.id), label: s.nameAr || s.name }))}
          />
          <ReportSelect label="نوع الفاتورة" value={invoiceType} onChange={setInvoiceType} options={config.typeOptions} />
          <Autocomplete
            size="small"
            options={products}
            value={product}
            onChange={(_, v) => setProduct(v)}
            getOptionLabel={(p) => p?.arabicName || p?.nameAr || ""}
            isOptionEqualToValue={(a, b) => a.productId === b.productId}
            noOptionsText="لا توجد نتائج"
            renderInput={(params) => <TextField {...params} label="الصنف" placeholder="الكل" sx={fieldSx} />}
          />
        </ReportFilters>
      }
    >
      {truncated && (
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: "12px" }}>
          النتائج كثيرة جدًا، فعُرض أول 20,000 سطر فقط. ضيّق الفترة أو اختر فرعًا أو صنفًا.
        </Alert>
      )}

      <ReportTableCard title="التفاصيل" count={fmtNumber(visibleRows.length)}>
        <DataGrid
          rows={visibleRows}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.id}
          slots={{ toolbar: GridToolbar }}
          initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
          pageSizeOptions={[25, 50, 100, 200]}
          disableRowSelectionOnClick
          getRowHeight={() => 52}
          getRowClassName={(p) => (p.row.invoiceType === config.returnType ? "row-return" : "")}
          localeText={{
            noRowsLabel: "لا توجد بيانات في هذه الفترة",
            toolbarColumns: "الأعمدة",
            toolbarFilters: "الفلاتر",
            toolbarDensity: "الكثافة",
            toolbarExport: "تصدير",
          }}
          sx={{ ...reportGridSx, "& .row-return": { bgcolor: "#fff7f8 !important" } }}
        />
      </ReportTableCard>
    </ReportPage>
  );
}
